import React from "react";
import Layout from "../components/layout/Layout";
import {
  FiTrendingUp, FiClock, FiStar, FiCheckCircle, FiInfo,
  FiLayers, FiTarget, FiZap, FiDollarSign, FiFilter, FiBookOpen, FiRefreshCw,
  FiShield, FiPieChart, FiGlobe, FiSliders, FiList
} from "react-icons/fi";


export default function BettingTips() {
  const color = {
    pageBg: "#f5f7fb",
    ink: "#0f172a",
    inkSub: "#475569",
    brand: "#2563eb",
    brandAlt: "#1d4ed8",
    border: "rgba(15, 23, 42, .10)",
    cardBg: "#ffffff",

    s1: "#0b1020",       // hero (dark)
    s2: "#ffffff",       // overview (white)
    s3: "#f3f6ff",       // filters (blue-tint)
    s4: "#ffffff",       // featured (white)
    s5: "#f8f9fb",       // live/upcoming (light gray)
    s6: "#fff7ed",       // strategy (warm tint)
    s7: "#eefdf5",       // methodology (green tint)
    s8: "#ffffff",       // glossary (white)
    s9: "#f8f9fb",       // FAQ (light gray)
  };

  const ui = {
    page: { background: color.pageBg, minHeight: "100vh" },
    container: { maxWidth: 1400, margin: "0 auto", padding: "28px 16px" },
    heroWrap: {
      position: "relative",
      padding: "80px 16px 80px",
      background:
        "radial-gradient(1200px 280px at 50% -30%, rgba(59,130,246,0.25), transparent 60%), linear-gradient(180deg, #0b1020 0%, #0b1020 40%, #0c1428 100%)",
      color: "white",
      borderBottom: "1px solid rgba(255,255,255,.06)",
    },
    heroInner: { maxWidth: 1350, margin: "0 auto" },
    badge: {
      display: "inline-flex", alignItems: "center", gap: 8,
      fontSize: 12, color: "#93c5fd", background: "rgba(147,197,253,.10)",
      border: "1px solid rgba(147,197,253,.30)", padding: "6px 12px", borderRadius: 999,
    },
    heroTitle: { margin: "14px 0 10px", fontSize: 36, lineHeight: 1.15, fontWeight: 900, color: '#fff', letterSpacing: "-0.02em" },
    heroSub: { margin: 0, color: "rgba(255,255,255,.85)", maxWidth: 820 },

    section: (bg) => ({ padding: "28px 16px", background: bg }),

    subnavWrap: {
      position: "sticky", top: 0, zIndex: 20, backdropFilter: "blur(6px)",
      background: "rgba(255,255,255,.8)", borderBottom: `1px solid ${color.border}`,
    },
    subnavInner: { maxWidth: 1400, margin: "0 auto", padding: "10px 16px" },
    subnavList: { display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" },
    subnavLink: {
      display: "inline-flex", alignItems: "center", gap: 8,
      padding: "8px 12px", borderRadius: 999, fontSize: 13,
      textDecoration: "none", color: color.ink,
      background: "#f1f5f9", border: `1px solid ${color.border}`,
    },

    card: {
      background: color.cardBg, border: `1px solid ${color.border}`,
      borderRadius: 16, padding: 18, color: color.ink, boxShadow: "0 1px 2px rgba(0,0,0,.04)",
    },
    cardHeader: {
      display: "flex", alignItems: "center", justifyContent: "space-between",
      marginBottom: 12, gap: 12, flexWrap: "wrap",
    },
    cardTitle: { margin: 0, fontWeight: 900, letterSpacing: "-0.02em", fontSize: 18 },
    cardSub: { margin: 0, color: color.inkSub, fontSize: 13 },

    btnPrimary: {
      height: 40, padding: "0 16px",
      background: `linear-gradient(180deg, ${color.brand}, ${color.brandAlt})`,
      color: "white", border: "1px solid rgba(37,99,235,.6)",
      borderRadius: 10, fontWeight: 700, cursor: "pointer",
    },
    btnGhost: {
      height: 40, padding: "0 14px",
      background: "transparent", color: color.ink,
      border: `1px solid ${color.border}`, borderRadius: 10, fontWeight: 700, cursor: "pointer",
    },
    chip: (active=false) => ({
      display: "inline-flex", alignItems: "center", gap: 8,
      padding: "8px 12px", borderRadius: 999, fontSize: 13, cursor: "pointer",
      background: active ? color.brand : "#f1f5f9",
      color: active ? "white" : color.ink, border: `1px solid ${active ? "#1e40af" : color.border}`,
    }),

    grid: { display: "grid", gap: 16, gridTemplateColumns: "repeat(12, minmax(0,1fr))" },
    col: (span) => ({ gridColumn: `span ${span} / span ${span}` }),

    tipCard: {
      background: "#fff", border: `1px solid ${color.border}`, borderRadius: 14, padding: 14,
      display: "grid", gap: 10, gridTemplateColumns: "2fr 2fr 1.2fr 1.2fr 1fr 1fr", alignItems: "center",
    },
    tipHead: { fontSize: 12, color: color.inkSub, fontWeight: 700 },
    tipCell: { fontSize: 14, color: color.ink },
    confPill: (lvl) => ({
      display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 10px", borderRadius: 999, fontSize: 12,
      background: lvl === 5 ? "rgba(16,185,129,.10)" : lvl === 4 ? "rgba(245,158,11,.12)" : "#eef2ff",
      color: lvl === 5 ? "#065f46" : lvl === 4 ? "#7c4603" : "#3730a3",
      border: `1px solid ${lvl === 5 ? "rgba(16,185,129,.35)" : lvl === 4 ? "rgba(245,158,11,.35)" : "rgba(55,48,163,.25)"}`
    }),

    leadTitle: { margin: 0, fontSize: 24, fontWeight: 900, color: color.ink },
    leadSub: { margin: 0, fontSize: 14, color: color.inkSub, maxWidth: 820 },
  };

  const types = [
    { id: "single", label: "Singles", icon: <FiTarget/> },
    { id: "acca", label: "Accumulators", icon: <FiLayers/> },
    { id: "value", label: "Value Bets", icon: <FiDollarSign/> },
    { id: "inplay", label: "In-Play", icon: <FiZap/> },
  ];

  const slots = [
    { id: "early", label: "Early (08:00–12:00 UTC)" },
    { id: "afternoon", label: "Afternoon (12:00–18:00 UTC)" },
    { id: "evening", label: "Evening (18:00–23:59 UTC)" },
    { id: "late", label: "Late (00:00–08:00 UTC)" },
  ];

  const categories = [
    { id: "ucl", label: "UCL" },
    { id: "epl", label: "Premier League" },
    { id: "laliga", label: "La Liga" },
    { id: "seriea", label: "Serie A" },
    { id: "bundesliga", label: "Bundesliga" },
    { id: "eredivisie", label: "Eredivisie" },
  ];

  const tips = [
    { id: "t1", kickOff: "2025-09-10T18:30:00Z", comp: "Premier League", home: "Arsenal", away: "Newcastle", market: "Over 2.5 Goals", pick: "Over 2.5", price: 1.95, confidence: 5, type: "single", slot: "evening", category: "epl", reasoning: "High-tempo sides, both top-5 xG/90 last 5; Arsenal home xG 2.1; Newcastle away xGA 1.8." },
    { id: "t2", kickOff: "2025-09-10T20:00:00Z", comp: "UCL", home: "Barcelona", away: "Napoli", market: "BTTS", pick: "Yes", price: 1.80, confidence: 4, type: "value", slot: "evening", category: "ucl", reasoning: "Barça concede in transitions; Napoli away non-pen xG 1.5; both >1.6 xG in last 3." },
    { id: "t3", kickOff: "2025-09-10T12:15:00Z", comp: "Serie A", home: "Inter", away: "Lazio", market: "Asian Handicap", pick: "Inter -0.5", price: 1.85, confidence: 4, type: "single", slot: "afternoon", category: "seriea", reasoning: "Inter PPDA trend ↑; Lazio short rest; right-channel overload mismatch." },
    { id: "t4", kickOff: "2025-09-10T19:45:00Z", comp: "La Liga", home: "Real Madrid", away: "Villarreal", market: "Corners (Home)", pick: "RM Over 5.5", price: 1.95, confidence: 3, type: "acca", slot: "evening", category: "laliga", reasoning: "Cross volume vs low block; Villarreal concede width; RM avg 7.1 corners last 10 home." },
  ];

  const [selectedType, setSelectedType] = React.useState("single");
  const [selectedSlot, setSelectedSlot] = React.useState("evening");
  const [selectedCats, setSelectedCats] = React.useState(["epl", "ucl"]);

  const toggleCat = (id) => setSelectedCats((prev) => prev.includes(id) ? prev.filter(c => c !== id) : [...prev, id]);
  const fmtKick = (iso) => new Date(iso).toUTCString().replace(":00 GMT", " UTC");

  const filtered = tips.filter(t => t.type === selectedType && t.slot === selectedSlot && selectedCats.includes(t.category));

  const Chip = ({ active, icon, children, onClick }) => (
    <button onClick={onClick} style={ui.chip(active)}>{icon}{children}</button>
  );

  const TipHeader = () => (
    <div style={{ ...ui.tipCard, background: "#fafafa" }}>
      <div style={ui.tipHead}>Match</div>
      <div style={ui.tipHead}>Market</div>
      <div style={ui.tipHead}>Pick</div>
      <div style={ui.tipHead}>Odds</div>
      <div style={ui.tipHead}>Kick-off</div>
      <div style={ui.tipHead}>Conf.</div>
    </div>
  );

  const TipRow = ({ t }) => (
    <div style={ui.tipCard}>
      <div style={ui.tipCell}><strong>{t.home}</strong> vs <strong>{t.away}</strong><div style={{ color: color.inkSub, fontSize: 12 }}>{t.comp}</div></div>
      <div style={ui.tipCell}>{t.market}</div>
      <div style={ui.tipCell}><FiCheckCircle style={{ verticalAlign: "text-bottom" }}/> {t.pick}</div>
      <div style={ui.tipCell}><FiTrendingUp style={{ verticalAlign: "text-bottom" }}/> {t.price.toFixed(2)}</div>
      <div style={ui.tipCell}><FiClock style={{ verticalAlign: "text-bottom" }}/> {fmtKick(t.kickOff)}</div>
      <div style={ui.tipCell}><span style={ui.confPill(t.confidence)}><FiStar/> {t.confidence}★</span></div>
      <div style={{ gridColumn: "1 / -1", color: color.inkSub, fontSize: 13, lineHeight: 1.5 }}>
        <FiInfo style={{ verticalAlign: "text-bottom" }}/> {t.reasoning}
      </div>
    </div>
  );

  return (
    <Layout headerStyle={1} footerStyle={1} breadcrumbTitle="Betting Tips">
      <section style={ui.heroWrap} aria-label="Hero">
        <div style={ui.heroInner}>
          <span style={ui.badge}><FiShield/> Elite Football Tips</span>
          <h1 style={ui.heroTitle}>Organized. Explainable. Actionable.</h1>
          <p style={ui.heroSub}>
            A world-class hub for betting insights. Navigate by type, time slot, and competition. Each pick includes market context,
            fair price guidance, kickoff time, and confidence rating backed by data.
          </p>
        </div>
      </section>

      <nav style={ui.subnavWrap} aria-label="Section navigation">
        <div style={ui.subnavInner}>
          <div style={ui.subnavList}>
            {[
              ["#overview", <FiList/>, "Overview"],
              ["#filters", <FiSliders/>, "Filters"],
              ["#featured", <FiStar/>, "Featured"],
              ["#upcoming", <FiTrendingUp/>, "Upcoming"],
              ["#strategy", <FiDollarSign/>, "Strategy"],
              ["#methodology", <FiPieChart/>, "Methodology"],
              ["#glossary", <FiBookOpen/>, "Glossary"],
              ["#faq", <FiInfo/>, "FAQ"],
            ].map(([href, icon, label]) => (
              <a key={href} href={href} style={ui.subnavLink}>{icon} {label}</a>
            ))}
          </div>
        </div>
      </nav>

      <section id="overview" style={ui.section(color.s2)} aria-label="Overview">
        <div style={ui.container}>
          <div style={{ display: "grid", gap: 14 }}>
            <h2 style={ui.leadTitle}><FiGlobe style={{verticalAlign:"text-bottom"}}/> Global Coverage</h2>
            <p style={ui.leadSub}>
              We prioritize high-liquidity leagues (UCL, EPL, La Liga, Serie A, Bundesliga, Eredivisie) to minimize spread and
              exploit durable edges. You can quickly jump to featured picks or refine filters for a tailored slate.
            </p>
          </div>
        </div>
      </section>

      <section id="filters" style={ui.section(color.s3)} aria-label="Filters">
        <div style={ui.container}>
          <div style={{ ...ui.card, display: "grid", gap: 16 }}>
            <div style={ui.cardHeader}>
              <div>
                <h3 style={ui.cardTitle}><FiFilter style={{ verticalAlign: "text-bottom" }}/> Refine Tips</h3>
                <p style={ui.cardSub}>Choose a betting <strong>type</strong>, a kickoff <strong>time slot</strong>, and one or more <strong>competitions</strong>.</p>
              </div>
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                <button style={ui.btnGhost} onClick={() => { setSelectedType("single"); setSelectedSlot("evening"); setSelectedCats(["epl","ucl"]); }}>Reset</button>
                <button style={ui.btnPrimary}><FiRefreshCw style={{ verticalAlign: "text-bottom" }}/> Refresh</button>
              </div>
            </div>

            <div>
              <div style={{ fontSize: 12, color: color.inkSub, marginBottom: 6, fontWeight: 700 }}>Type</div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {types.map(t => (
                  <Chip key={t.id} active={selectedType === t.id} icon={<span style={{ display: "inline-flex", alignItems: "center" }}>{t.icon}</span>} onClick={() => setSelectedType(t.id)}>{t.label}</Chip>
                ))}
              </div>
            </div>

            <div>
              <div style={{ fontSize: 12, color: color.inkSub, marginBottom: 6, fontWeight: 700 }}>Time slot</div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {slots.map(s => (
                  <Chip key={s.id} active={selectedSlot === s.id} icon={<FiClock/>} onClick={() => setSelectedSlot(s.id)}>{s.label}</Chip>
                ))}
              </div>
            </div>

            <div>
              <div style={{ fontSize: 12, color: color.inkSub, marginBottom: 6, fontWeight: 700 }}>Competitions</div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {categories.map(c => (
                  <Chip key={c.id} active={selectedCats.includes(c.id)} icon={<FiTarget/>} onClick={() => toggleCat(c.id)}>{c.label}</Chip>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="featured" style={ui.section(color.s4)} aria-label="Featured Picks">
        <div style={ui.container}>
          <div style={{ ...ui.card, display: "grid", gap: 12 }}>
            <div style={ui.cardHeader}>
              <div>
                <h3 style={ui.cardTitle}><FiStar style={{ verticalAlign: "text-bottom" }}/> Featured Picks</h3>
                <p style={ui.cardSub}>Editor-curated selections with the highest conviction and robust signal support.</p>
              </div>
            </div>
            <TipHeader/>
            {tips.sort((a,b) => b.confidence - a.confidence).slice(0,2).map(t => <TipRow t={t} key={t.id}/>) }
          </div>
        </div>
      </section>

      <section id="upcoming" style={ui.section(color.s5)} aria-label="Live & Upcoming">
        <div style={ui.container}>
          <div style={{ ...ui.card, display: "grid", gap: 12 }}>
            <div style={ui.cardHeader}>
              <div>
                <h3 style={ui.cardTitle}><FiTrendingUp style={{ verticalAlign: "text-bottom" }}/> Live & Upcoming</h3>
                <p style={ui.cardSub}>Filtered view based on your selections. Adjust filters above to explore more edges.</p>
              </div>
              <small style={{ color: color.inkSub }}>Showing: <strong>{selectedType}</strong> • <strong>{selectedSlot}</strong> • {selectedCats.join(", ").toUpperCase()}</small>
            </div>
            <TipHeader/>
            {filtered.length ? (
              filtered.map(t => <TipRow t={t} key={t.id} />)
            ) : (
              <div style={{ padding: 14, background: "#fafafa", borderRadius: 12, border: `1px dashed ${color.border}`, color: color.inkSub }}>
                No tips for this filter set. Try a different <em>type</em>, <em>slot</em> or add more <em>competitions</em>.
              </div>
            )}
          </div>
        </div>
      </section>

      <section id="strategy" style={ui.section(color.s6)} aria-label="Strategy & Bankroll">
        <div style={ui.container}>
          <div style={{ ...ui.card, display: "grid", gap: 10 }}>
            <div style={ui.cardHeader}>
              <div>
                <h3 style={ui.cardTitle}><FiDollarSign/> Bankroll & Execution</h3>
                <p style={ui.cardSub}>Simple, defensible rules to survive variance and compound your edge.</p>
              </div>
            </div>
            <ul style={{ margin: 0, paddingLeft: 18, color: color.inkSub, lineHeight: 1.8 }}>
              <li>Flat stake 1–2% per pick; max 3% on 5★ edges.</li>
              <li>Never chase; downswings cluster — stay size-disciplined.</li>
              <li>Track <strong>CLV</strong> (Closing Line Value); long-run profit correlates with beating the close.</li>
              <li>Diversify across markets and time slots to smooth variance.</li>
            </ul>
          </div>
        </div>
      </section>

      <section id="methodology" style={ui.section(color.s7)} aria-label="Methodology">
        <div style={ui.container}>
          <div style={{ ...ui.card, display: "grid", gap: 10 }}>
            <div style={ui.cardHeader}>
              <div>
                <h3 style={ui.cardTitle}><FiPieChart/> Methodology</h3>
                <p style={ui.cardSub}>Signals, validation steps, and publishing rules behind every selection.</p>
              </div>
            </div>
            <div style={{ color: color.inkSub, lineHeight: 1.7 }}>
              <p><strong>Signals:</strong> xG/xGA trends, shot quality/locations, pace, rest/travel, tactical matchups, injury clusters, weather, and market microstructure.</p>
              <p><strong>Validation:</strong> picks must clear a minimum price vs. model fair; multi-signal agreement boosts confidence; manual sanity-check before publish.</p>
              <p><strong>Publishing:</strong> we state the <em>minimum playable price</em>. If live odds fall below it, we prefer a pass over forcing action.</p>
            </div>
          </div>
        </div>
      </section>

      <section id="glossary" style={ui.section(color.s8)} aria-label="Glossary">
        <div style={ui.container}>
          <div style={{ ...ui.card, display: "grid", gap: 12 }}>
            <div style={ui.cardHeader}><div><h3 style={ui.cardTitle}><FiBookOpen/> Glossary</h3><p style={ui.cardSub}>Quick definitions for common terms and markets.</p></div></div>
            {[
              ["1X2", "Home win / Draw / Away win."],
              ["BTTS", "Both Teams To Score."],
              ["xG / xGA", "Expected goals for/against; chance quality."],
              ["Asian Handicap", "Spread that balances matchups; pushes possible."],
              ["CLV", "Closing Line Value — did you beat the final market price?"],
            ].map(([term, def], i) => (
              <div key={i} style={{ border: `1px solid ${color.border}`, borderRadius: 12, padding: 12, background: "#fff" }}>
                <div style={{ fontWeight: 800 }}>{term}</div>
                <div style={{ color: color.inkSub }}>{def}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="faq" style={{ padding: "24px 16px 40px", background: color.s9 }} aria-label="FAQ">
        <div style={ui.container}>
          <div style={{ ...ui.card, display: "grid", gap: 12 }}>
            <div style={ui.cardHeader}><div><h3 style={ui.cardTitle}><FiInfo/> FAQ</h3><p style={ui.cardSub}>Common questions about confidence, pricing, and expectations.</p></div></div>
            {[
              ["How are confidence stars assigned?", "Blend of model edge vs. market plus qualitative checks. 5★ = multi-signal agreement + stable price."],
              ["Do you guarantee profits?", "No. Tips are educational signals. Edges realize over many bets; bankroll discipline is essential."],
              ["What if the odds drop?", "If price falls below our minimum, we pass. Price integrity beats action frequency."],
            ].map(([q, a], i) => (
              <div key={i} style={{ border: `1px solid ${color.border}`, borderRadius: 12, padding: 12, background: "#fff" }}>
                <div style={{ fontWeight: 800 }}>{q}</div>
                <div style={{ color: color.inkSub }}>{a}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </Layout>
  );
}
